import { createSlice, createAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    ReadingAnalytics,
    WaveformData,
    AudioComparison
} from '@/services/reading/types';

// === ANALYTICS ACTIONS ===
export const fetchReadingAnalyticsRequest = createAction<number>('readingAnalytics/fetchAnalyticsRequest');
export const fetchReadingAnalyticsSuccess = createAction<ReadingAnalytics>('readingAnalytics/fetchAnalyticsSuccess');
export const fetchReadingAnalyticsFailure = createAction<string>('readingAnalytics/fetchAnalyticsFailure');

export const fetchWeeklyProgressRequest = createAction<number>('readingAnalytics/fetchWeeklyProgressRequest');
export const fetchWeeklyProgressSuccess = createAction<ReadingAnalytics['weekly_progress']>('readingAnalytics/fetchWeeklyProgressSuccess');
export const fetchWeeklyProgressFailure = createAction<string>('readingAnalytics/fetchWeeklyProgressFailure');

export const fetchMonthlyProgressRequest = createAction<number>('readingAnalytics/fetchMonthlyProgressRequest');
export const fetchMonthlyProgressSuccess = createAction<ReadingAnalytics['monthly_progress']>('readingAnalytics/fetchMonthlyProgressSuccess');
export const fetchMonthlyProgressFailure = createAction<string>('readingAnalytics/fetchMonthlyProgressFailure');

export const fetchSkillBreakdownRequest = createAction('readingAnalytics/fetchSkillBreakdownRequest');
export const fetchSkillBreakdownSuccess = createAction<ReadingAnalytics['skill_breakdown']>('readingAnalytics/fetchSkillBreakdownSuccess');
export const fetchSkillBreakdownFailure = createAction<string>('readingAnalytics/fetchSkillBreakdownFailure');

// === WAVEFORM ACTIONS ===
export const fetchWaveformDataRequest = createAction<string>('readingAnalytics/fetchWaveformDataRequest');
export const fetchWaveformDataSuccess = createAction<WaveformData>('readingAnalytics/fetchWaveformDataSuccess');
export const fetchWaveformDataFailure = createAction<string>('readingAnalytics/fetchWaveformDataFailure');

// === AUDIO COMPARISON ACTIONS ===
export const compareReadingAudioRequest = createAction<{ userAudioUrl: string; referenceAudioUrl?: string; targetSentence?: string }>('readingAnalytics/compareAudioRequest');
export const compareReadingAudioSuccess = createAction<AudioComparison>('readingAnalytics/compareAudioSuccess');
export const compareReadingAudioFailure = createAction<string>('readingAnalytics/compareAudioFailure');

// === PROGRESS TRACKING ACTIONS ===
export const fetchProgressTimelineRequest = createAction<number>('readingAnalytics/fetchProgressTimelineRequest');
export const fetchProgressTimelineSuccess = createAction<Array<{
    date: string;
    sessions_count: number;
    total_time: number;
    avg_score: number;
    passages_completed: number;
}>>('readingAnalytics/fetchProgressTimelineSuccess');
export const fetchProgressTimelineFailure = createAction<string>('readingAnalytics/fetchProgressTimelineFailure');

export const fetchStreakDataRequest = createAction('readingAnalytics/fetchStreakDataRequest');
export const fetchStreakDataSuccess = createAction<{
    current_streak: number;
    longest_streak: number;
    streak_dates: string[];
    total_practice_days: number;
}>('readingAnalytics/fetchStreakDataSuccess');
export const fetchStreakDataFailure = createAction<string>('readingAnalytics/fetchStreakDataFailure');

export const fetchSkillTrendsRequest = createAction('readingAnalytics/fetchSkillTrendsRequest');
export const fetchSkillTrendsSuccess = createAction<{
    pronunciation_trend: Array<{ date: string, score: number }>;
    fluency_trend: Array<{ date: string, score: number }>;
    comprehension_trend: Array<{ date: string, score: number }>;
}>('readingAnalytics/fetchSkillTrendsSuccess');
export const fetchSkillTrendsFailure = createAction<string>('readingAnalytics/fetchSkillTrendsFailure');

// === STATE INTERFACE ===
export interface ReadingAnalyticsState {
    // Analytics
    analytics: ReadingAnalytics | null;
    isLoadingAnalytics: boolean;
    analyticsError: string | null;

    // Weekly progress
    weeklyProgress: ReadingAnalytics['weekly_progress'];
    isLoadingWeeklyProgress: boolean;
    weeklyProgressError: string | null;

    // Monthly progress
    monthlyProgress: ReadingAnalytics['monthly_progress'];
    isLoadingMonthlyProgress: boolean;
    monthlyProgressError: string | null;

    // Skill breakdown
    skillBreakdown: ReadingAnalytics['skill_breakdown'] | null;
    isLoadingSkillBreakdown: boolean;
    skillBreakdownError: string | null;

    // Waveform data
    waveformData: WaveformData | null;
    isLoadingWaveform: boolean;
    waveformError: string | null;

    // Audio comparison
    audioComparison: AudioComparison | null;
    isComparingAudio: boolean;
    audioComparisonError: string | null;

    // Progress timeline
    progressTimeline: Array<{
        date: string;
        sessions_count: number;
        total_time: number;
        avg_score: number;
        passages_completed: number;
    }>;
    isLoadingProgressTimeline: boolean;
    progressTimelineError: string | null;

    // Streak data
    streakData: {
        current_streak: number;
        longest_streak: number;
        streak_dates: string[];
        total_practice_days: number;
    } | null;
    isLoadingStreakData: boolean;
    streakDataError: string | null;

    // Skill trends
    skillTrends: {
        pronunciation_trend: Array<{ date: string, score: number }>;
        fluency_trend: Array<{ date: string, score: number }>;
        comprehension_trend: Array<{ date: string, score: number }>;
    } | null;
    isLoadingSkillTrends: boolean;
    skillTrendsError: string | null;
}

// === INITIAL STATE ===
const initialState: ReadingAnalyticsState = {
    // Analytics
    analytics: null,
    isLoadingAnalytics: false,
    analyticsError: null,

    // Weekly progress
    weeklyProgress: [],
    isLoadingWeeklyProgress: false,
    weeklyProgressError: null,

    // Monthly progress
    monthlyProgress: [],
    isLoadingMonthlyProgress: false,
    monthlyProgressError: null,

    // Skill breakdown
    skillBreakdown: null,
    isLoadingSkillBreakdown: false,
    skillBreakdownError: null,

    // Waveform data
    waveformData: null,
    isLoadingWaveform: false,
    waveformError: null,

    // Audio comparison
    audioComparison: null,
    isComparingAudio: false,
    audioComparisonError: null,

    // Progress timeline
    progressTimeline: [],
    isLoadingProgressTimeline: false,
    progressTimelineError: null,

    // Streak data
    streakData: null,
    isLoadingStreakData: false,
    streakDataError: null,

    // Skill trends
    skillTrends: null,
    isLoadingSkillTrends: false,
    skillTrendsError: null,
};

// === SLICE DEFINITION ===
const readingAnalyticsSlice = createSlice({
    name: 'readingAnalytics',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === ANALYTICS ===
            .addCase(fetchReadingAnalyticsRequest, (state) => {
                state.isLoadingAnalytics = true;
                state.analyticsError = null;
            })
            .addCase(fetchReadingAnalyticsSuccess, (state, action) => {
                state.isLoadingAnalytics = false;
                state.analytics = action.payload;
            })
            .addCase(fetchReadingAnalyticsFailure, (state, action) => {
                state.isLoadingAnalytics = false;
                state.analyticsError = action.payload;
            })

            // === WEEKLY PROGRESS ===
            .addCase(fetchWeeklyProgressRequest, (state) => {
                state.isLoadingWeeklyProgress = true;
                state.weeklyProgressError = null;
            })
            .addCase(fetchWeeklyProgressSuccess, (state, action) => {
                state.isLoadingWeeklyProgress = false;
                state.weeklyProgress = action.payload;
            })
            .addCase(fetchWeeklyProgressFailure, (state, action) => {
                state.isLoadingWeeklyProgress = false;
                state.weeklyProgressError = action.payload;
            })

            // === MONTHLY PROGRESS ===
            .addCase(fetchMonthlyProgressRequest, (state) => {
                state.isLoadingMonthlyProgress = true;
                state.monthlyProgressError = null;
            })
            .addCase(fetchMonthlyProgressSuccess, (state, action) => {
                state.isLoadingMonthlyProgress = false;
                state.monthlyProgress = action.payload;
            })
            .addCase(fetchMonthlyProgressFailure, (state, action) => {
                state.isLoadingMonthlyProgress = false;
                state.monthlyProgressError = action.payload;
            })

            // === SKILL BREAKDOWN ===
            .addCase(fetchSkillBreakdownRequest, (state) => {
                state.isLoadingSkillBreakdown = true;
                state.skillBreakdownError = null;
            })
            .addCase(fetchSkillBreakdownSuccess, (state, action) => {
                state.isLoadingSkillBreakdown = false;
                state.skillBreakdown = action.payload;
            })
            .addCase(fetchSkillBreakdownFailure, (state, action) => {
                state.isLoadingSkillBreakdown = false;
                state.skillBreakdownError = action.payload;
            })

            // === WAVEFORM DATA ===
            .addCase(fetchWaveformDataRequest, (state) => {
                state.isLoadingWaveform = true;
                state.waveformError = null;
            })
            .addCase(fetchWaveformDataSuccess, (state, action) => {
                state.isLoadingWaveform = false;
                state.waveformData = action.payload;
            })
            .addCase(fetchWaveformDataFailure, (state, action) => {
                state.isLoadingWaveform = false;
                state.waveformError = action.payload;
            })

            // === AUDIO COMPARISON ===
            .addCase(compareReadingAudioRequest, (state) => {
                state.isComparingAudio = true;
                state.audioComparisonError = null;
            })
            .addCase(compareReadingAudioSuccess, (state, action) => {
                state.isComparingAudio = false;
                state.audioComparison = action.payload;
            })
            .addCase(compareReadingAudioFailure, (state, action) => {
                state.isComparingAudio = false;
                state.audioComparisonError = action.payload;
            })

            // === PROGRESS TIMELINE ===
            .addCase(fetchProgressTimelineRequest, (state) => {
                state.isLoadingProgressTimeline = true;
                state.progressTimelineError = null;
            })
            .addCase(fetchProgressTimelineSuccess, (state, action) => {
                state.isLoadingProgressTimeline = false;
                state.progressTimeline = action.payload;
            })
            .addCase(fetchProgressTimelineFailure, (state, action) => {
                state.isLoadingProgressTimeline = false;
                state.progressTimelineError = action.payload;
            })

            // === STREAK DATA ===
            .addCase(fetchStreakDataRequest, (state) => {
                state.isLoadingStreakData = true;
                state.streakDataError = null;
            })
            .addCase(fetchStreakDataSuccess, (state, action) => {
                state.isLoadingStreakData = false;
                state.streakData = action.payload;
            })
            .addCase(fetchStreakDataFailure, (state, action) => {
                state.isLoadingStreakData = false;
                state.streakDataError = action.payload;
            })

            // === SKILL TRENDS ===
            .addCase(fetchSkillTrendsRequest, (state) => {
                state.isLoadingSkillTrends = true;
                state.skillTrendsError = null;
            })
            .addCase(fetchSkillTrendsSuccess, (state, action) => {
                state.isLoadingSkillTrends = false;
                state.skillTrends = action.payload;
            })
            .addCase(fetchSkillTrendsFailure, (state, action) => {
                state.isLoadingSkillTrends = false;
                state.skillTrendsError = action.payload;
            });
    },
});

export default readingAnalyticsSlice.reducer; 